import { Contract } from "@abacus/ows-contract-client";
import { OwsAbacusContractClient } from "@abacus/ows-contract-client/fetch";
import { AbacusEventName, TargetType } from "@abacus/ows-event-client";
import { OwsAbacusEventClient } from "@abacus/ows-event-client/fetch";
import {
  type AbacusState,
  AbacusStateActionName,
  AbacusStateActionStatus,
  ContractType,
  ParentTableName,
} from "@abacus/ows-state-client";
import { OwsAbacusStateClient } from "@abacus/ows-state-client/fetch";

import { Logger } from "@abacus/common";
import { ResourceNotFoundError } from "@/resourceNotFoundError";
import { ResourceCreationError } from "@/resourceCreationError";
import { Runner } from "@/types";

import { SAPSyncContractTaskConfig } from "./types";
import { Throttler } from "@abacus/throttlers";

const MAX_HOURS_MS = 5 * 60 * 60 * 1000;

export class SAPSyncContractTask implements Runner {
  contractClient: OwsAbacusContractClient;
  eventClient: OwsAbacusEventClient;
  logger?: Logger;
  resync: boolean;
  stateClient: OwsAbacusStateClient;
  throttler?: Throttler;

  constructor({
    contractClient,
    eventClient,
    logger,
    resync,
    stateClient,
    throttler,
  }: SAPSyncContractTaskConfig) {
    this.contractClient = contractClient;
    this.eventClient = eventClient;
    this.logger = logger;
    this.resync = resync ?? false;
    this.stateClient = stateClient;
    this.throttler = throttler;
  }

  async run(contractId: number): Promise<void> {
    // Get or create SAP sync state
    const state = await this.getOrCreateSapSyncState(contractId);

    // Check if already synced
    const isSynced = state.action_status == AbacusStateActionStatus.complete;

    // Check if fresh state
    const isFresh =
      state.action_status == AbacusStateActionStatus.init &&
      Date.now() - new Date(state.created_at).getTime() < MAX_HOURS_MS;

    this.logger?.info(
      [
        isSynced
          ? `Already synced:`
          : isFresh
            ? `Already syncing:`
            : `Triggering sync:`,
        `Contract ID: ${contractId},`,
        `State ID: ${state.abacus_state_id},`,
        `Action name: ${state.action_name},`,
        `Action status: ${state.action_status}`,
      ].join(" "),
    );

    // Skip if already synced / syncing,
    // unless explicitly resyncing
    if ((isSynced || isFresh) && !this.resync) {
      return;
    }

    // Trigger the SAP sync
    await this?.throttler?.wait();
    const { data: event } = await this.eventClient.createAbacusEvent({
      event_name: AbacusEventName.sap_sync_contract,
      target_id: contractId,
      target_type: TargetType.contract,
    });

    this.logger?.info(
      [
        `Sync triggered:`,
        `Contract ID: ${contractId},`,
        `Event ID: ${event.abacus_event_id},`,
        `Event date: ${event.event_date}`,
      ].join(" "),
    );
  }

  private async getContract(contractId: number): Promise<Contract> {
    try {
      const response = await this.contractClient.getContract({ contractId });
      return response.data;
    } catch (_) {
      throw new ResourceNotFoundError("contract", contractId);
    }
  }

  private async getStates(contractId: number): Promise<AbacusState[]> {
    try {
      const response = await this.stateClient.getAbacusStatesByParentTable({
        parentTableId: contractId,
        parentTableName: ParentTableName.contract,
      });
      return response.data;
    } catch (_) {
      throw new ResourceNotFoundError("contract states", contractId);
    }
  }

  /**
   * Get or create a contract's `sap_sync` state.
   * The state must exist for a SAP sync to run.
   *
   * See: lambda-abacus/lambda/sync_contract_sap/sync_contract_sap/processor.py
   *
   * @param contract The target contract
   *
   * @returns The contract's `sap_sync` state
   */
  private async getOrCreateSapSyncState(
    contractId: number,
  ): Promise<AbacusState> {
    // Get contract states
    let states = await this.getStates(contractId);

    // Get target state
    let state = states.find((state) => {
      return state.action_name == AbacusStateActionName.sap_sync;
    });

    // Check if the state exists
    if (state != null) {
      return state;
    }
    // Get the contract
    const contract = await this.getContract(contractId);
    // Create and get contract states
    const statesResponse =
      await this.stateClient.createAbacusStatesByParentTable(
        {
          parentTableId: contract.contract_id,
          parentTableName: ParentTableName.contract,
        },
        { contract_type: contract.contract_type as ContractType },
      );
    // Get target state
    states = statesResponse.data;
    state = states.find((state) => {
      return state.action_name == AbacusStateActionName.sap_sync;
    });
    // Check if the state was created
    if (state == null) {
      throw new ResourceCreationError("abacus_state");
    }
    // Return the state
    return state;
  }
}
